import { Injectable } from "@angular/core";
import { BehaviorSubject, Observable, of, throwError } from "rxjs";
import { HttpClient } from "@angular/common/http";
import { catchError, map, switchMap, take, tap } from "rxjs/operators";
import { environment } from "environments/environment";
import { BaseRespone, PaginationResult } from "app/core/models/common.response";
import { ReplacementCost } from "./replacement-cost.types";

@Injectable({
    providedIn: 'root'
})
export class ReplacementCostService {
    // Private
    private _replacementCost: BehaviorSubject<ReplacementCost | null> = new BehaviorSubject(null);
    private _replacementCosts: BehaviorSubject<ReplacementCost[] | null> = new BehaviorSubject(null);
    constructor(private _httpClient: HttpClient) {
    }

    /**
     * Getter for replacementCost
     */
    get replacementCost$(): Observable<ReplacementCost> {
        return this._replacementCost.asObservable();
    }

    /**
     * Getter for replacementCost
     */
    get replacementCosts$(): Observable<ReplacementCost[]> {
        return this._replacementCosts.asObservable();
    }

    /**
     * Get replacementCost
     */
    getReplacementCosts(reportId: number): Observable<BaseRespone<ReplacementCost[]>> {
        return this._httpClient.get<BaseRespone<ReplacementCost[]>>(`${environment.baseUrl}/Report/GetReplacementCostlist/${reportId}`).pipe(
            tap((response) => {
                this._replacementCosts.next(response.result);
            })
        );
    }

    /**
      * Create replacementCost
      */
    createReplacementCost(replacementCost: ReplacementCost): Observable<BaseRespone<ReplacementCost>> {
        return this.replacementCosts$.pipe(
            take(1),
            switchMap(replacementCosts => this._httpClient.post<BaseRespone<ReplacementCost>>(`${environment.baseUrl}/Report/AddReplacementCost`, replacementCost)
                .pipe(
                    catchError((error) => {
                        return of(error.error);
                    }),
                    map((response) => {
                        // Return the new product
                        if (response.isSuccess) {
                            this._replacementCosts.next([response.result, ...replacementCosts]);

                        }
                        return response;
                    })
                ))
        );
    }

    /**
     * Get board
     *
     * @param id
     */
    getReplacementCost(id: number): Observable<BaseRespone<ReplacementCost>> {
        return this._httpClient.get(`${environment.baseUrl}/Report/GetReplacementCost/${id}`).pipe(
            catchError((error) => {
                return of(error.error);
            }),
            map(response => response),
            tap(board => this._replacementCost.next(board.result))
        );
    }


    /**
    * Get contact by id
    */
    getReplacementCostById(id: number): Observable<ReplacementCost> {
        return this._replacementCosts.pipe(
            take(1),
            map((replacementCosts) => {

                // Find the contact
                const replacementCost = replacementCosts.find(item => item.id === id) || null;

                // Update the replacementCost
                this._replacementCost.next(replacementCost);

                // Return the replacementCost
                return replacementCost;
            }),
            switchMap((replacementCost) => {

                if (!replacementCost) {
                    return throwError('Could not found replacementCost with id of ' + id + '!');
                }

                return of(replacementCost);
            })
        );
    }

    /**
     * Update the card
     *
     * @param id
     * @param card
     */
    updateReplacementCost(replacementCost: ReplacementCost): Observable<BaseRespone<ReplacementCost>> {
        return this.replacementCosts$.pipe(
            take(1),
            switchMap(replacementCosts => this._httpClient.put<BaseRespone<ReplacementCost>>(`${environment.baseUrl}/Report/UpdateReplacementCost`, replacementCost).pipe(
                map((replacementCostCard) => {
                    const index = replacementCosts.findIndex(item => item.id === replacementCost.id);

                    // Update the product
                    if (replacementCostCard.isSuccess) {
                        replacementCosts[index] = replacementCostCard.result;

                        // Update the products
                        this._replacementCosts.next(replacementCosts);

                        this._replacementCost.next(replacementCostCard.result);
                    }
                    // Return the updated card
                    return replacementCostCard;
                }), catchError((error) => {
                    return of(error.error);
                })
            ))
        );
    }

    deleteReplacementCost(id: number): Observable<BaseRespone<boolean>> {
        return this.replacementCosts$.pipe(
            take(1),
            switchMap(replacementCosts => this._httpClient.delete(`${environment.baseUrl}/Report/DeleteReplacementCost/${id}`).pipe(
                map((deleted: BaseRespone<boolean>) => {

                    // Find the index of the deleted product
                    const index = replacementCosts.findIndex(item => item.id === id);

                    // Delete the product
                    replacementCosts.splice(index, 1);

                    // Update the products
                    this._replacementCosts.next(replacementCosts);

                    // Return the deleted status
                    return deleted;
                })
            ))
        );
    }

    deleteAllReplacementCost(ids: number[]): Observable<BaseRespone<boolean>> {
        const requestBody = { ids: ids };

        return this.replacementCosts$.pipe(
            take(1),
            switchMap(replacementCost => this._httpClient.delete(`${environment.baseUrl}/Report/DeleteAllReplacementCost`, {
                body: requestBody
            }).pipe(
                map((deleted: BaseRespone<boolean>) => {

                    ids.forEach(id => {
                        // Find the index of the deleted product
                        const index = replacementCost.findIndex(item => item.id === id);
                        // Delete the product
                        replacementCost.splice(index, 1);
                    });
                    // Update the products
                    this._replacementCosts.next(replacementCost);

                    // Return the deleted status
                    return deleted;
                })
            ))
        );

    }
}
